import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, RefreshCw, Bus, Phone, Mail, CalendarX2, EyeOff } from 'lucide-react';
import { WeeklyDateView } from '@/components/all-trips/WeeklyDateView';
import { TypeLegend } from '@/components/all-trips/TypeLegend';
import { typeCodesInPlan } from '@/components/all-trips/tripType';
import { PeriodPicker, PeriodValue, weekLabel, weekFromLabel } from '@/components/all-trips/PeriodPicker';
import { usePublicLanguage } from '@/components/public/usePublicLanguage';
import PublicLanguageToggle from '@/components/public/PublicLanguageToggle';
import { addDays, isoLocal, mondayOf } from '@/components/routeplans/routeplanWeek';
import { abbreviateLocation } from '@/lib/abbreviations';
import { makeLocationColorResolver } from '@/lib/locationColors';

const API = process.env.REACT_APP_BACKEND_URL;
const MAX_DAYS = 184;

// Kundens bussplan (/kp/:token): offentlig, uten innlogging, speiler intern Ukesplan i lesemodus. Live — hentes på nytt ved hver visning.
const Brand = ({ company, toggle }: { company: any; toggle: React.ReactNode }) => (
  <div className="relative text-center mb-5" data-testid="kp-brand">
    <div className="absolute right-0 top-0">{toggle}</div>
    {company?.logo && <img src={company.logo} alt={company.name || ''} className="h-12 mx-auto mb-2 object-contain" />}
    {company?.name && <p className="text-sm font-semibold text-slate-500 uppercase tracking-wider">{company.name}</p>}
    {(company?.phone || company?.email) && (
      <p className="text-xs text-slate-400 mt-1 inline-flex items-center gap-3">
        {company.phone && <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" />{company.phone}</span>}
        {company.email && <span className="inline-flex items-center gap-1"><Mail className="w-3 h-3" />{company.email}</span>}
      </p>
    )}
  </div>
);

const StateCard = ({ company, title, message, toggle }: any) => (
  <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
    <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8 text-center" data-testid="kp-state-card">
      <Brand company={company} toggle={toggle} />
      <h1 className="text-lg font-bold text-slate-800 mb-1">{title}</h1>
      <p className="text-sm text-slate-500">{message}</p>
    </div>
  </div>
);

function periodFromParams(sp: URLSearchParams): PeriodValue {
  const fra = sp.get('fra'), til = sp.get('til'), uke = sp.get('uke');
  if (fra && til) return { mode: 'range', weekStart: isoLocal(mondayOf(new Date(`${fra}T00:00:00`))), from: fra, to: til };
  const ws = (uke && weekFromLabel(uke)) || isoLocal(mondayOf(new Date()));
  return { mode: 'week', weekStart: ws, from: ws, to: isoLocal(addDays(new Date(`${ws}T00:00:00`), 6)) };
}

export default function PublicCustomerPlanPage() {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const [sp, setSp] = useSearchParams();
  const period = useMemo(() => periodFromParams(sp), [sp]);
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [err, setErr] = useState<string>('');
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const { lang, setPublicLanguage } = usePublicLanguage(data?.preferred_language);
  const toggle = <PublicLanguageToggle lang={lang} onChange={setPublicLanguage} />;

  // Ikke indekserbar: offentlig, men uten innlogging — kun for den som har lenken.
  useEffect(() => {
    const meta = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null;
    const prev = meta?.content;
    if (meta) meta.content = 'noindex, nofollow';
    return () => { if (meta && prev !== undefined) meta.content = prev; };
  }, []);

  const load = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setErr('');
    try {
      const q = period.mode === 'range' ? `fra=${period.from}&til=${period.to}` : `uke=${weekLabel(period.weekStart)}`;
      const res = await fetch(`${API}/api/public/customer-plan/${token}?${q}`, { cache: 'no-store' });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (res.status === 400) setErr(j?.detail || t('publicCustomerPlan.ugyldigPeriode', 'Ugyldig periode'));
        else { setErr('invalid'); setData(null); }
        return;
      }
      setData(j); setUpdatedAt(new Date());
    } catch { setErr(silent ? t('publicCustomerPlan.kunneIkkeOppdatere', 'Kunne ikke oppdatere akkurat nå – viser sist hentede plan') : 'invalid'); }
    finally { setLoading(false); setRefreshing(false); }
  }, [token, period.mode, period.from, period.to, period.weekStart, t]);
  useEffect(() => { load(); }, [load]);

  const onPeriodChange = (v: PeriodValue) => {
    const next = new URLSearchParams();
    if (v.mode === 'range') { next.set('fra', v.from); next.set('til', v.to); } else next.set('uke', weekLabel(v.weekStart));
    const l = sp.get('lang'); if (l) next.set('lang', l);
    setSp(next, { replace: true });
  };

  // Samme forkortelses- og fargeregler som intern Ukesplan (lib/abbreviations + useAllTrips.getLocationColor), kart fra backend.
  const locShort = useMemo(() => {
    const map: Record<string, string> = data?.location_shortcuts || {};
    return (loc: string) => abbreviateLocation(loc, map);
  }, [data?.location_shortcuts]);
  const locColor = useMemo(() => makeLocationColorResolver(data?.location_colors, data?.location_shortcuts), [data?.location_colors, data?.location_shortcuts]);
  const maxStops = useMemo(() => Math.max(2, ...((data?.days || []).flatMap((d: any) => (d.trips || []).map((tr: any) => tr.stops?.length || 0)))), [data]);
  const noop = () => {};

  if (loading && !data) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }
  if ((err === 'invalid' || !data) && !refreshing) {
    return <StateCard company={{}} toggle={toggle} title={t('publicCustomerPlan.lenkenErIkkeGyldig', 'Lenken er ikke gyldig')} message={t('publicCustomerPlan.lenkenErUgyldigEllerUtlopt', 'Lenken er ugyldig eller utløpt. Kontakt busselskapet for en ny lenke.')} />;
  }
  const company = data.company || {};
  if (data.state === 'revoked') return <StateCard company={company} toggle={toggle} title={t('publicCustomerPlan.lenkenErTrukketTilbake', 'Lenken er ikke lenger gyldig')} message={t('publicCustomerPlan.kontaktForNyLenke', 'Bussplan-lenken er trukket tilbake. Kontakt busselskapet for en ny lenke.')} />;
  if (data.state === 'expired') return <StateCard company={company} toggle={toggle} title={t('publicCustomerPlan.lenkenErUtlopt', 'Lenken er utløpt')} message={t('publicCustomerPlan.kontaktForNyLenke', 'Bussplan-lenken er trukket tilbake. Kontakt busselskapet for en ny lenke.')} />;
  if (data.state === 'not_yet_active') return <StateCard company={company} toggle={toggle} title={t('publicCustomerPlan.ikkeAktivEnna', 'Lenken er ikke aktiv ennå')} message={t('publicCustomerPlan.komTilbakeSenere', 'Kom tilbake senere.')} />;

  // Historikk vises alltid; fremtidige dager kun når de er publisert — også publiserte dager uten turer («Ingen turer denne dagen»).
  const days = (data.days || []).filter((d: any) => d.visible ?? (d.is_history || d.published || (d.trips || []).length > 0));
  const hiddenFuture = (data.days || []).filter((d: any) => d.visible === false).length;
  const plan = { ...data, days };
  const today = new Date();
  const minDate = isoLocal(addDays(today, -MAX_DAYS)), maxDate = isoLocal(addDays(today, MAX_DAYS));
  const clock = updatedAt ? updatedAt.toLocaleTimeString(lang === 'en' ? 'en-GB' : 'nb-NO', { hour: '2-digit', minute: '2-digit' }) : '';

  return (
    <div className="min-h-screen bg-slate-50 py-6 px-3 sm:px-6" data-testid="kp-page">
      <div className="max-w-7xl mx-auto">
        <Brand company={company} toggle={toggle} />
        <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-5 mb-4">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h1 className="text-xl font-bold text-slate-800 inline-flex items-center gap-2" data-testid="kp-title"><Bus className="w-5 h-5 text-teal-600" />{t('publicCustomerPlan.bussplan', 'Bussplan')}</h1>
              {data.customer_name && <p className="text-sm text-slate-500 mt-0.5" data-testid="kp-customer">{data.customer_name}</p>}
              {data.shared && <p className="text-sm text-slate-500 mt-0.5" data-testid="kp-shared">{t('publicCustomerPlan.deltBussplan', 'Publisert bussplan – alle turer')}</p>}
            </div>
            <div className="text-right text-xs text-slate-500">
              <button type="button" onClick={() => load(true)} disabled={refreshing} className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900 font-medium" data-testid="kp-refresh">
                <RefreshCw className={`w-3.5 h-3.5 ${refreshing ? 'animate-spin' : ''}`} />{t('publicCustomerPlan.oppdater', 'Oppdater')}
              </button>
              {clock && <div className="mt-0.5" data-testid="kp-updated-at">{t('publicCustomerPlan.sistOppdatert', 'Sist oppdatert')} {clock}</div>}
            </div>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <PeriodPicker value={period} onChange={onPeriodChange} maxDays={data.max_days || MAX_DAYS} minDate={minDate} maxDate={maxDate} testIdPrefix="kp" />
            <div className="text-sm font-semibold text-slate-700" data-testid="kp-summary">
              {t('publicCustomerPlan.oppsummering', { defaultValue: '{{trips}} turer, {{pax}} passasjerer', trips: data.total_trips || 0, pax: data.total_passengers || 0 })}
            </div>
          </div>
          {err && err !== 'invalid' && <p className="mt-2 text-xs text-red-600" data-testid="kp-range-error">{err}</p>}
          {hiddenFuture > 0 && (
            <p className="mt-2 text-xs text-slate-500 inline-flex items-center gap-1" data-testid="kp-hidden-days">
              <EyeOff className="w-3 h-3" />{t('publicCustomerPlan.skjulteDager', { defaultValue: '{{count}} kommende dag(er) i perioden er ikke publisert ennå og vises når busselskapet publiserer dem.', count: hiddenFuture })}
            </p>
          )}
        </div>

        {days.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-10 text-center text-slate-500" data-testid="kp-empty">
            <CalendarX2 className="w-10 h-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">{t('publicCustomerPlan.ingenTurerIValgtPeriode', 'Ingen publiserte dager å vise i valgt periode ennå')}</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="kp-grid">
            <TypeLegend codes={typeCodesInPlan(plan)} lang={lang} testId="kp-type-legend" />
            <WeeklyDateView
              weeklyPlan={plan} isStaff={false} readOnly hideNotes maxStopsInWeek={maxStops}
              selectedDrivers={[]} showUnassignedOnly={false}
              selectedTrips={new Set()} setSelectedTrips={noop} expandedRows={new Set()} setExpandedRows={noop}
              getLocationColor={locColor} getLocationShortName={locShort}
              getDriverColor={() => null} getDriverDisplayName={(n: string) => n} getCompanyShortName={(n: string) => n}
              handleViewDetails={noop} setSeatRequestModal={noop}
            />
          </div>
        )}
        <p className="text-[11px] text-slate-400 text-center mt-6">{t('publicCustomerPlan.fotnote', 'Planen oppdateres fortløpende av busselskapet. Innstilte turer vises overstrøket.')}</p>
      </div>
    </div>
  );
}
